import { useMemo, useState, useCallback, useRef, useReducer } from "react";
import toWords from "split-camelcase-to-words";
import Editor from "@monaco-editor/react";
import { Button, Form } from "react-bootstrap";
import { Virtuoso } from "react-virtuoso";
import moment from "moment";
import cloneDeep from "lodash.clonedeep";
import { useDoRef } from "../../hooks/useDataRef";
import { useDataSaveItem, useDataDeleteItem } from "../../hooks/useData";
import { showConflicts, useReloadItems } from "../../hooks/draft";
import {
  conflictOf,
  movedUnder,
  staleOf,
  staleOfConflict,
  versionOf
} from "../../hooks/baseVersion";
import usePermissions from "../../hooks/usePermissions";
import Container from "../container";
import ColleagueSaved from "../colleagueSaved";
import ContentType from "../contentType";
import styles from "./styles.module.scss";

const notSave = [
  "aresInternals",
  "metadata",
  "labelsAll",
  "policies",
  "health",
  "users",
  "notes",
  "help"
];

// The rows are the stored ones when the list is computed (a filter, a
// table, a reload, a save): a colleague's save shows at the next one. The
// editor of a row keeps what is typed in it (its entry: the text, the item
// to save, the row the edits started from): Save and Delete send the
// version of that row (baseVersion), and a row computed again with another
// version (a colleague's) does not replace the text: a warning says who
// and when, Reload puts the stored row in the editor. A refused write
// (409) opens the conflict dialog; the edits stay.
export default function DataMapExplorer({ data, canSave = false }) {
  const { write, destroy } = usePermissions();
  // conflictDialog: a refused write (409) opens the conflict dialog
  const deleteItem = useDataDeleteItem({
    confirm: false,
    notify: true,
    conflictDialog: true
  });
  const saveItem = useDataSaveItem({
    confirm: false,
    notify: true,
    conflictDialog: true
  });
  const reloadItems = useReloadItems();
  const foldRef = useRef(new Map());
  const [currentTable, setCurrentTable] = useState(0);
  const [idFilter, setIdFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [volatileFilter, setVolatileFilter] = useState(false);
  const [catalog, setCatalog] = useState("");
  // The editors' entries, by row id: { text, current, base, conflict }
  const refMap = useRef(new Map());
  // The list computed again (the stored rows), the rows drawn again
  const [refreshKey, refresh] = useReducer((n) => n + 1, 0);
  const [drawKey, redraw] = useReducer((n) => n + 1, 0);

  const entryOf = useCallback((id) => {
    if (!refMap.current.has(id)) refMap.current.set(id, {});
    return refMap.current.get(id);
  }, []);

  // The stored row in the editor, the edits discarded
  const reloadRow = useCallback(
    async (table, id) => {
      const { failed = [] } =
        (await reloadItems([{ accessor: table, id }])) ?? {};
      if (failed.length)
        throw new Error(
          `The stored ${table} ${id} could not be loaded: try again.`
        );
      refMap.current.delete(id);
      refresh();
    },
    [reloadItems]
  );

  // A refused write: a version conflict keeps the edits, with the warning
  // and the conflict dialog; other refusals are notified by useDataRemote
  const refused = useCallback(
    (table, id, result) => {
      if (result?.type !== "conflict") return;
      entryOf(id).conflict = staleOfConflict(conflictOf(result, table, id));
      redraw();
      showConflicts({
        conflicts: result.conflicts,
        onDiscard: () => reloadRow(table, id)
      });
    },
    [entryOf, reloadRow]
  );

  const tables = useMemo(() => {
    if (data) {
      return [...data.keys()].sort();
    }

    return [];
  }, [data]);

  const tableData = useMemo(() => {
    if (tables) {
      const key = tables[currentTable];
      return [...data.get(key).values()]
        .filter((k) => {
          if (idFilter && !k.id.includes(idFilter)) return false;
          if (titleFilter && !(k.title ?? "").includes(titleFilter))
            return false;
          if (volatileFilter && k.volatile !== true) return false;
          if (catalog && k.catalog !== catalog) return false;
          return true;
        })
        .sort((a, b) => {
          if (a.title < b.title) return -1;
          if (a.title > b.title) return 1;
          return 0;
        });
    }
    // refreshKey: the stored rows again (a save, a reload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    catalog,
    currentTable,
    data,
    idFilter,
    refreshKey,
    tables,
    titleFilter,
    volatileFilter
  ]);

  const drawRow = useCallback(
    (index) => {
      const data = tableData[index];
      const table = tables[currentTable];
      const entry = entryOf(data.id);
      // The version the edits started from (or the row's, unedited)
      const baseVersion = versionOf(entry.base ?? data);
      const stale =
        entry.conflict ??
        (entry.base && movedUnder(entry.base, data) ? staleOf(data) : null);

      return (
        <Container
          key={data.id}
          extra={<span className={styles.dataId}>{data.id}</span>}
          title={
            <span className={styles.dataTitle}>{data.title ?? data.id}</span>
          }
          header={
            <div className={styles.header}>
              <span className={styles.dataUsername}>
                {data.lastSaved?.username}
              </span>
              <span className={styles.dataDate}>
                {data.lastSaved?.date
                  ? moment(data.lastSaved?.date).format("DD-MM-YYYY")
                  : null}
              </span>
            </div>
          }
          headerJustify="flex-end"
          onFold={(f) => foldRef.current.set(index, f)}
          folded={foldRef.current.get(index) ?? true}
          foldable={true}
          footer={
            <>
              {!notSave.includes(tables[currentTable]) &&
              destroy.includes("db") &&
              canSave ? (
                <div className={styles.dataFooter}>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this item?"
                        ) === true
                      ) {
                        deleteItem(
                          {
                            accessor: table,
                            Id: data.id,
                            reference: data.reference,
                            baseVersion
                          },
                          undefined,
                          (status, result) => refused(table, data.id, result)
                        );
                      }
                    }}
                    style={{ marginRight: "5px" }}
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
              {!notSave.includes(tables[currentTable]) &&
              write.includes("db") &&
              canSave ? (
                <div className={styles.dataFooter}>
                  <ColleagueSaved
                    stale={stale}
                    onReload={() => reloadRow(table, data.id)}
                  />
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={() => {
                      if (entry.current) {
                        saveItem(
                          {
                            accessor: table,
                            Id: data.id,
                            prevId: data.id,
                            isNew: false,
                            data: entry.current,
                            baseVersion
                          },
                          // The saved row in the editor: the next edits
                          // start from it
                          () => {
                            refMap.current.delete(data.id);
                            refresh();
                          },
                          (status, result) => refused(table, data.id, result)
                        );
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              ) : null}
            </>
          }
          className={styles.dataContainer}
        >
          <EditorSave entry={entry} data={data} accessor={table} />
        </Container>
      );
    },
    // drawKey: an entry changed (a refused write)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      canSave,
      currentTable,
      deleteItem,
      destroy,
      drawKey,
      entryOf,
      refused,
      reloadRow,
      saveItem,
      tableData,
      tables,
      write
    ]
  );

  if (tables.length === 0) return null;

  return (
    <div className={styles.container}>
      <div className={styles.tables}>
        {tables.map((table, index) => {
          return (
            <div
              className={`${styles.table} ${
                index === currentTable ? styles.active : ""
              }`}
              key={toWords(table)}
              onClick={() => {
                refMap.current = new Map();
                setCurrentTable(index);
              }}
            >
              {table}
              <div
                className={`${styles.total} ${
                  index === currentTable ? styles.active : ""
                }`}
              >
                {data.get(table).size}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.data}>
        <div className={styles.search}>
          <span className={styles.label}>Id</span>
          <Form.Control
            type="text"
            placeholder="Id..."
            value={idFilter}
            onChange={(e) => setIdFilter(e.target.value)}
            className={styles.inputId}
          />
          <span className={styles.label}>Title</span>
          <Form.Control
            type="text"
            placeholder="Title..."
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            className={styles.input}
          />
          {tables[currentTable] === "metadata" && (
            <>
              <Form.Check
                type="switch"
                id="volatile-switch"
                label="Volatile"
                value={volatileFilter}
                onChange={(e) => setVolatileFilter(e.target.checked)}
                className={styles.volatileSwitch}
              />
              <span className={styles.catalogBox}>
                <ContentType
                  rootNode={{ contentType: catalog }}
                  onlySelect={true}
                  allowAllType={true}
                  onChange={(c) => setCatalog(c)}
                />
              </span>
            </>
          )}
        </div>
        <div className={styles.dataBox}>
          <Virtuoso
            style={{ height: "648px" }}
            totalCount={tableData?.length}
            itemContent={drawRow}
          />
        </div>
      </div>
    </div>
  );
}

// entry: what is typed (text), the item to save (current, with its refs),
// the row the edits started from (base)
const EditorSave = ({ data, accessor, entry }) => {
  const errorRef = useRef(false);
  const doRefs = useDoRef();

  const onMount = useCallback((editor, monaco) => {
    editor.trigger("fold", "editor.foldLevel2");
    editor.setScrollPosition({ scrollTop: 0 });
    editor.revealLine(1);
  }, []);

  const onChange = useCallback(
    (text) => {
      if (entry.text === undefined) entry.base = cloneDeep(data);
      entry.text = text;
      if (!errorRef.current) {
        try {
          const parsed = JSON.parse(text);
          entry.current = doRefs(accessor, parsed);
        } catch (e) {}
      }
    },
    [accessor, data, doRefs, entry]
  );

  return (
    <Editor
      width="100%"
      height="400px"
      defaultLanguage="json"
      // The text typed is never replaced by the row
      value={entry.text ?? JSON.stringify(data, null, 2)}
      theme="vs-dark"
      onMount={onMount}
      onChange={onChange}
      onValidate={(m) => (errorRef.current = m.length > 0)}
      options={{ minimap: { enabled: false }, folding: true }}
    />
  );
};
